import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Clock, Fuel, BarChart3, TrendingUp, TrendingDown,
  AlertTriangle, Download, Settings2, ChevronDown, ChevronUp,
  Activity, Gauge, Layers, Truck, MapPin, Check
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ProductionReport() {
  const { user, selectedTenantId, getHeaders: ctxGetHeaders } = useAuth();
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedMachineIds, setSelectedMachineIds] = useState([]);
  const [locationId, setLocationId] = useState('');
  const [machines, setMachines] = useState([]);
  const [locations, setLocations] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [configModo, setConfigModo] = useState('estimado');
  const [showConfig, setShowConfig] = useState(false);
  const [expandedOp, setExpandedOp] = useState(null);
  const [showMachineDropdown, setShowMachineDropdown] = useState(false);
  const machineDropdownRef = useRef(null);

  const getHeaders = useCallback(() => ctxGetHeaders(), [ctxGetHeaders]);

  const fetchMachines = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/machines`, { headers: getHeaders() });
      if (res.ok) setMachines(await res.json());
    } catch {}
  }, [getHeaders]);

  const fetchLocations = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/locations`, { headers: getHeaders() });
      if (res.ok) setLocations(await res.json());
    } catch {}
  }, [getHeaders]);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/producao/config-mensuracao`, { headers: getHeaders() });
      if (res.ok) {
        const d = await res.json();
        setConfigModo(d.modo_mensuracao_consumo || 'estimado');
      }
    } catch {}
  }, [getHeaders]);

  useEffect(() => {
    if (user && selectedTenantId) {
      fetchMachines();
      fetchLocations();
      fetchConfig();
    }
  }, [user, selectedTenantId, fetchMachines, fetchLocations, fetchConfig]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClick = (e) => {
      if (machineDropdownRef.current && !machineDropdownRef.current.contains(e.target)) {
        setShowMachineDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const toggleMachine = (id) => {
    setSelectedMachineIds(prev =>
      prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]
    );
  };

  const selectAllMachines = () => {
    if (selectedMachineIds.length === machines.length) {
      setSelectedMachineIds([]);
    } else {
      setSelectedMachineIds(machines.map(m => m.id));
    }
  };

  const fetchReport = async () => {
    if (!dateFrom || !dateTo) { toast.error('Informe o período'); return; }
    setLoading(true);
    try {
      let url = `${API}/api/producao/relatorio-equiparacao-v2?date_from=${dateFrom}&date_to=${dateTo}`;
      if (selectedMachineIds.length > 0 && selectedMachineIds.length < machines.length) {
        url += `&maquina_ids=${selectedMachineIds.join(',')}`;
      }
      if (locationId) url += `&location_id=${locationId}`;
      const res = await fetch(url, { headers: getHeaders() });
      if (res.ok) {
        setData(await res.json());
      } else {
        toast.error('Erro ao carregar relatório');
      }
    } catch { toast.error('Erro de conexão'); }
    setLoading(false);
  };

  const updateModo = async (modo) => {
    try {
      const res = await fetch(`${API}/api/producao/config-mensuracao`, {
        method: 'PUT',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ modo_mensuracao_consumo: modo })
      });
      if (res.ok) { setConfigModo(modo); toast.success(`Modo alterado para ${modo === 'estimado' ? 'Estimado' : 'Real'}`); }
    } catch { toast.error('Erro ao atualizar configuração'); }
  };

  const downloadPdf = () => {
    window.open(`${API}/api/producao/relatorio-equiparacao-v2/pdf?tenant_id=${selectedTenantId || localStorage.getItem('selectedTenantId') || ''}&date_from=${dateFrom}&date_to=${dateTo}${selectedMachineIds.length > 0 ? `&maquina_ids=${selectedMachineIds.join(',')}` : ''}${locationId ? `&location_id=${locationId}` : ''}`, '_blank');
  };

  const machineLabel = selectedMachineIds.length === 0 || selectedMachineIds.length === machines.length
    ? 'Todas as Máquinas'
    : `${selectedMachineIds.length} máquina${selectedMachineIds.length > 1 ? 's' : ''} selecionada${selectedMachineIds.length > 1 ? 's' : ''}`;

  const r = data?.resumo;

  return (
    <div className="space-y-6" data-testid="production-report-v2">
      {/* FILTROS */}
      <div className="bg-[#141418] border border-white/10 rounded-xl p-5">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Data Início</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="bg-[#0C0C0E] border border-white/10 text-white rounded-lg px-3 py-2 text-sm"
              data-testid="filter-date-from" />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Data Fim</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="bg-[#0C0C0E] border border-white/10 text-white rounded-lg px-3 py-2 text-sm"
              data-testid="filter-date-to" />
          </div>

          {/* Multi-select Machine Filter */}
          <div className="relative" ref={machineDropdownRef}>
            <label className="block text-xs text-gray-400 mb-1">Máquinas</label>
            <button
              onClick={() => setShowMachineDropdown(!showMachineDropdown)}
              className="bg-[#0C0C0E] border border-white/10 text-white rounded-lg px-3 py-2 text-sm min-w-[220px] flex items-center justify-between gap-2"
              data-testid="filter-machine-multi">
              <span className="truncate text-left">{machineLabel}</span>
              <ChevronDown size={14} className={`text-gray-400 transition-transform ${showMachineDropdown ? 'rotate-180' : ''}`} />
            </button>
            {showMachineDropdown && (
              <div className="absolute z-50 mt-1 w-full bg-[#1A1A1E] border border-white/10 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                <button
                  onClick={selectAllMachines}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-white/5 flex items-center gap-2 border-b border-white/5"
                  data-testid="filter-machine-select-all">
                  <div className={`w-4 h-4 rounded border flex items-center justify-center ${selectedMachineIds.length === machines.length ? 'bg-[#D4790A] border-[#D4790A]' : 'border-gray-600'}`}>
                    {selectedMachineIds.length === machines.length && <Check size={10} className="text-black" />}
                  </div>
                  <span className="text-gray-300 font-medium">Selecionar Todas</span>
                </button>
                {machines.map(m => (
                  <button
                    key={m.id}
                    onClick={() => toggleMachine(m.id)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-white/5 flex items-center gap-2"
                    data-testid={`filter-machine-${m.id}`}>
                    <div className={`w-4 h-4 rounded border flex items-center justify-center ${selectedMachineIds.includes(m.id) ? 'bg-[#D4790A] border-[#D4790A]' : 'border-gray-600'}`}>
                      {selectedMachineIds.includes(m.id) && <Check size={10} className="text-black" />}
                    </div>
                    <span className="text-gray-300">{m.serial_number} {m.brand} {m.model}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Location Filter */}
          <div>
            <label className="block text-xs text-gray-400 mb-1">Local</label>
            <select value={locationId} onChange={e => setLocationId(e.target.value)}
              className="bg-[#0C0C0E] border border-white/10 text-white rounded-lg px-3 py-2 text-sm min-w-[180px]"
              data-testid="filter-location">
              <option value="">Todos os Locais</option>
              {locations.map(l => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          </div>

          <button onClick={fetchReport} disabled={loading}
            className="bg-gradient-to-r from-[#D4790A] to-[#B5680A] text-black font-semibold px-6 py-2 rounded-lg hover:shadow-lg hover:shadow-orange-500/20 transition-all disabled:opacity-50"
            data-testid="btn-generate-report">
            {loading ? 'Gerando...' : 'Gerar Relatório'}
          </button>
          <button onClick={() => setShowConfig(!showConfig)}
            className="text-gray-400 hover:text-white p-2 rounded-lg hover:bg-white/5 transition-all"
            title="Configurações" data-testid="btn-config">
            <Settings2 size={18} />
          </button>
          {data && (
            <button onClick={downloadPdf}
              className="flex items-center gap-2 bg-white/5 border border-white/10 text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all text-sm"
              data-testid="btn-download-pdf">
              <Download size={16} /> PDF
            </button>
          )}
        </div>

        {/* CONFIG PANEL */}
        {showConfig && (
          <div className="mt-4 pt-4 border-t border-white/10">
            <p className="text-xs text-gray-400 mb-2">Modo de Mensuração de Consumo</p>
            <div className="flex gap-3">
              <button onClick={() => updateModo('estimado')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${configModo === 'estimado' ? 'bg-[#D4790A] text-black' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}
                data-testid="btn-modo-estimado">
                Estimado (Média Global)
              </button>
              <button onClick={() => updateModo('real')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${configModo === 'real' ? 'bg-[#D4790A] text-black' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}
                data-testid="btn-modo-real">
                Real (Diesel Apontado)
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {configModo === 'estimado'
                ? 'O diesel por operação será estimado usando o consumo global médio (L/h) x horas de cada operação.'
                : 'O diesel será segregado por operação usando os apontamentos reais de abastecimento por máquina.'}
            </p>
          </div>
        )}
      </div>

      {/* SEM DADOS */}
      {data && r && r.total_horas === 0 && (
        <div className="bg-[#141418] border border-white/10 rounded-xl p-8 text-center">
          <Layers size={40} className="text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">Nenhuma O.S. operacional encontrada no período.</p>
          <p className="text-xs text-gray-500 mt-1">Verifique se existem O.S. fechadas com horímetro no intervalo selecionado.</p>
        </div>
      )}

      {/* RESULTADOS */}
      {data && r && r.total_horas > 0 && (
        <>
          {/* 3 PILARES */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <PillarCard icon={<Clock size={22} />} label="Total Horas" value={`${r.total_horas.toLocaleString('pt-BR')} h`} color="blue" />
            <PillarCard icon={<Fuel size={22} />} label="Total Diesel" value={`${r.total_diesel.toLocaleString('pt-BR')} L`} color="orange" />
            <PillarCard icon={<BarChart3 size={22} />} label={`Produção (${r.unidade_producao})`}
              value={r.total_producao > 0 ? r.total_producao.toLocaleString('pt-BR') : '—'} color="green" />
          </div>

          {/* INDICADORES GLOBAIS */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Consumo Global" value={`${r.consumo_global_lh} L/h`} icon={<Gauge size={16} />} />
            <KpiCard label="Produtividade" value={r.prod_por_hora_global > 0 ? `${r.prod_por_hora_global} ${r.unidade_producao?.toLowerCase()}/h` : '—'} icon={<TrendingUp size={16} />} />
            <KpiCard label={`L/${r.unidade_producao}`} value={r.consumo_por_unidade_global > 0 ? `${r.consumo_por_unidade_global}` : '—'} icon={<Fuel size={16} />} />
            <KpiCard label={`R$/${r.unidade_producao}`} value={r.custo_por_unidade_global > 0 ? `R$ ${r.custo_por_unidade_global.toFixed(2)}` : '—'} icon={<Activity size={16} />} />
          </div>

          <div className="text-xs text-gray-500 flex items-center gap-2 px-1 flex-wrap">
            <span className="w-2 h-2 rounded-full bg-[#D4790A]" />
            Método: {data.modo_mensuracao === 'estimado' ? 'Estimado (média global L/h)' : 'Real (diesel apontado)'}
            <span className="mx-2">|</span> {r.total_os} O.S. | {r.total_maquinas} máquinas
            {selectedMachineIds.length > 0 && selectedMachineIds.length < machines.length && (
              <><span className="mx-2">|</span> Filtro: {selectedMachineIds.length} máquina(s)</>
            )}
          </div>

          {/* POR TIPO DE OPERAÇÃO */}
          <div className="bg-[#141418] border border-white/10 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-white/10">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Layers size={16} className="text-[#D4790A]" /> Detalhamento por Tipo de Operação
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="table-by-operation">
                <thead>
                  <tr className="text-xs text-gray-400 border-b border-white/5">
                    <th className="text-left py-3 px-4">Operação</th>
                    <th className="text-right py-3 px-3">Horas</th>
                    <th className="text-right py-3 px-3">% Horas</th>
                    <th className="text-right py-3 px-3">Diesel Est.</th>
                    <th className="text-right py-3 px-3">L/h</th>
                    <th className="text-right py-3 px-3">Produção</th>
                    <th className="text-right py-3 px-3">{r.unidade_producao}/h</th>
                    <th className="text-right py-3 px-3">L/{r.unidade_producao}</th>
                    <th className="text-right py-3 px-3">R$/{r.unidade_producao}</th>
                    <th className="py-3 px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.por_tipo_operacao.map((op, i) => (
                    <React.Fragment key={op.tipo_operacao_id}>
                      <tr className="border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer"
                          onClick={() => setExpandedOp(expandedOp === i ? null : i)}>
                        <td className="py-3 px-4 font-medium text-white">{op.tipo_operacao_nome}</td>
                        <td className="py-3 px-3 text-right text-gray-300">{op.horas.toFixed(1)}</td>
                        <td className="py-3 px-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-12 h-1.5 bg-white/10 rounded-full overflow-hidden">
                              <div className="h-full bg-[#D4790A] rounded-full" style={{width: `${op.percentual_horas}%`}} />
                            </div>
                            <span className="text-gray-400 text-xs">{op.percentual_horas}%</span>
                          </div>
                        </td>
                        <td className="py-3 px-3 text-right text-gray-300">{op.diesel_estimado.toFixed(1)} L</td>
                        <td className="py-3 px-3 text-right text-gray-300">{op.consumo_lh.toFixed(2)}</td>
                        <td className="py-3 px-3 text-right text-gray-300">{op.producao > 0 ? op.producao.toFixed(1) : '—'}</td>
                        <td className="py-3 px-3 text-right text-gray-300">{op.prod_por_hora > 0 ? op.prod_por_hora.toFixed(2) : '—'}</td>
                        <td className="py-3 px-3 text-right text-gray-300">{op.consumo_por_unidade > 0 ? op.consumo_por_unidade.toFixed(1) : '—'}</td>
                        <td className="py-3 px-3 text-right text-gray-300">{op.custo_por_unidade > 0 ? `R$ ${op.custo_por_unidade.toFixed(2)}` : '—'}</td>
                        <td className="py-3 px-2 text-gray-500">
                          {expandedOp === i ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </td>
                      </tr>
                      {expandedOp === i && (
                        <tr><td colSpan={10} className="bg-[#0C0C0E] px-6 py-4">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                            <div><span className="text-gray-500">O.S.:</span> <span className="text-white ml-1">{op.qtd_os}</span></div>
                            <div><span className="text-gray-500">Máquinas:</span> <span className="text-white ml-1">{op.qtd_maquinas}</span></div>
                            <div><span className="text-gray-500">Horas/Unid:</span> <span className="text-white ml-1">{op.horas_por_unidade > 0 ? op.horas_por_unidade.toFixed(2) : '—'}</span></div>
                            <div><span className="text-gray-500">Custo Diesel:</span> <span className="text-white ml-1">R$ {op.custo_diesel.toFixed(2)}</span></div>
                          </div>
                        </td></tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* POR MÁQUINA */}
          <div className="bg-[#141418] border border-white/10 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-white/10">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Truck size={16} className="text-[#D4790A]" /> Desempenho por Máquina
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="table-by-machine">
                <thead>
                  <tr className="text-xs text-gray-400 border-b border-white/5">
                    <th className="text-left py-3 px-4">Máquina</th>
                    <th className="text-right py-3 px-3">Horas</th>
                    <th className="text-right py-3 px-3">Diesel (L)</th>
                    <th className="text-right py-3 px-3">L/h</th>
                    <th className="text-right py-3 px-3">Custo</th>
                    <th className="text-right py-3 px-3">R$/h</th>
                    <th className="text-right py-3 px-3">Produção</th>
                    <th className="text-right py-3 px-3">{r.unidade_producao}/h</th>
                    <th className="text-right py-3 px-3">L/{r.unidade_producao}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.por_maquina.map(m => {
                    const acima = m.consumo_lh > r.consumo_global_lh * 1.15;
                    return (
                      <tr key={m.machine_id} className={`border-b border-white/5 hover:bg-white/5 transition-colors ${acima ? 'bg-red-500/5' : ''}`}>
                        <td className="py-3 px-4 font-medium text-white text-xs">{m.machine_name}</td>
                        <td className="py-3 px-3 text-right text-gray-300">{m.horas.toFixed(1)}</td>
                        <td className="py-3 px-3 text-right text-gray-300">{m.diesel.toFixed(1)}</td>
                        <td className="py-3 px-3 text-right">
                          <span className={acima ? 'text-red-400 font-semibold' : 'text-gray-300'}>{m.consumo_lh.toFixed(2)}</span>
                        </td>
                        <td className="py-3 px-3 text-right text-gray-300">R$ {m.custo_diesel.toLocaleString('pt-BR', {minimumFractionDigits:0})}</td>
                        <td className="py-3 px-3 text-right text-gray-300">R$ {m.custo_por_hora.toFixed(0)}</td>
                        <td className="py-3 px-3 text-right text-gray-300">{m.producao > 0 ? m.producao.toFixed(1) : '—'}</td>
                        <td className="py-3 px-3 text-right text-gray-300">{m.prod_por_hora > 0 ? m.prod_por_hora.toFixed(2) : '—'}</td>
                        <td className="py-3 px-3 text-right text-gray-300">{m.consumo_por_unidade > 0 ? m.consumo_por_unidade.toFixed(1) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* ANÁLISE DE DESEMPENHO */}
          {data.analise_desempenho?.length > 0 && (
            <div className="bg-[#141418] border border-white/10 rounded-xl p-5 space-y-3">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <AlertTriangle size={16} className="text-[#D4790A]" /> Análise de Desempenho
              </h3>
              {data.analise_desempenho.map((a, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-white/5 rounded-lg border border-white/5">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    a.tipo === 'consumo_alto' ? 'bg-orange-500/10 text-orange-400'
                    : a.tipo === 'maquina_consumo_alto' ? 'bg-red-500/10 text-red-400'
                    : 'bg-blue-500/10 text-blue-400'
                  }`}>
                    {a.tipo.includes('consumo') ? <Fuel size={16} /> : <TrendingDown size={16} />}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">{a.titulo}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{a.descricao}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function PillarCard({ icon, label, value, color }) {
  const colors = {
    blue: 'from-blue-500/10 to-blue-600/5 border-blue-500/20',
    orange: 'from-orange-500/10 to-orange-600/5 border-orange-500/20',
    green: 'from-green-500/10 to-green-600/5 border-green-500/20',
  };
  const iconColors = { blue: 'text-blue-400', orange: 'text-orange-400', green: 'text-green-400' };
  return (
    <div className={`bg-gradient-to-br ${colors[color]} border rounded-xl p-5`} data-testid={`pillar-${color}`}>
      <div className="flex items-center justify-between mb-2">
        <span className={iconColors[color]}>{icon}</span>
        <span className="text-xs text-gray-500 uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
    </div>
  );
}

function KpiCard({ label, value, icon }) {
  return (
    <div className="bg-[#141418] border border-white/10 rounded-lg p-3">
      <div className="flex items-center gap-1.5 text-gray-500 mb-1">
        {icon} <span className="text-xs">{label}</span>
      </div>
      <p className="text-lg font-bold text-white">{value}</p>
    </div>
  );
}
